// src/store/api/propertyApi.ts - FINAL FIXED VERSION
import type {
  ApiResponse,
  BulkUpdatePropertyDto,
  CreatePropertyDto,
  PaginatedPropertyResponseDto,
  PropertyFilterDto,
  PropertyResponseDto,
  PropertyStatsDto,
  UpdatePropertyDto,
} from "../../types/property";
import {baseApi} from "./baseApi";

export const propertyApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Create Property
    createProperty: builder.mutation<PropertyResponseDto, CreatePropertyDto>({
      query: (data) => ({
        url: "/properties",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [{type: "Property", id: "LIST"}, "Property"],
    }),

    // Get Properties with filters
    getProperties: builder.query<
      ApiResponse<PaginatedPropertyResponseDto>,
      PropertyFilterDto
    >({
      query: (params) => ({
        url: "/properties",
        params,
      }),
      transformResponse: (response: any) => {
        if (response.success && response.data && response.data.data) {
          // Transform each property in the array
          const transformedData = response.data.data.map((property: any) => ({
            ...property,
            price: parseFloat(property.price) || 0,
            bedrooms: parseInt(property.bedrooms) || 0,
            bathrooms: parseInt(property.bathrooms) || 0,
            reads: parseInt(property.reads) || 0,
            latitude: property.latitude ? parseFloat(property.latitude) : null,
            longitude: property.longitude ? parseFloat(property.longitude) : null,
          }));

          return {
            ...response,
            data: {
              ...response.data,
              data: transformedData,
            },
          };
        }
        return response;
      },
      providesTags: (result) =>
        result && result.data && Array.isArray(result.data.data)
          ? [
              ...result.data.data.map(({id}) => ({type: "Property" as const, id})),
              {type: "Property", id: "LIST"},
            ]
          : [{type: "Property", id: "LIST"}],
    }),

    // ✅ FINAL FIX: Create Property with Photos - Prevent JSON serialization
    createPropertyWithPhotos: builder.mutation<
      PropertyResponseDto,
      {
        data: CreatePropertyDto;
        files?: File[];
      }
    >({
      query: ({data, files = []}) => {
        console.log("🚀 RTK Query createPropertyWithPhotos called");
        console.log("📊 Input data:", {data, filesCount: files.length});

        const formData = new FormData();

        // ✅ Add all property data as form fields
        Object.entries(data).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            // Handle boolean values
            if (typeof value === "boolean") {
              formData.append(key, value.toString());
            }
            // Handle number values
            else if (typeof value === "number") {
              formData.append(key, value.toString());
            }
            // Handle string values
            else {
              formData.append(key, String(value));
            }
          }
        });

        // ✅ Add files - Backend expects 'files' field name
        files.forEach((file) => {
          formData.append("files", file);
        });

        console.log("📦 FormData created with entries:");
        for (let [key, value] of formData.entries()) {
          if (value instanceof File) {
            console.log(`  ${key}: File(${value.name}, ${value.size} bytes)`);
          } else {
            console.log(`  ${key}: ${value}`);
          }
        }

        // ✅ CRITICAL: Use prepareHeaders to ensure proper content-type
        interface CreatePropertyWithPhotosQueryArgs {
          data: CreatePropertyDto;
          files?: File[];
        }

        interface CreatePropertyWithPhotosQueryReturn {
          url: string;
          method: "POST";
          body: FormData;
          prepareHeaders: (headers: Headers) => Headers;
        }

        return {
          url: "/properties",
          method: "POST",
          body: formData,
          // ✅ IMPORTANT: Add this to prevent RTK Query from JSON serializing FormData
          prepareHeaders: (headers: Headers): Headers => {
            // Remove any content-type header to let browser set multipart/form-data automatically
            headers.delete("content-type");
            return headers;
          },
        } as CreatePropertyWithPhotosQueryReturn;
      },
      invalidatesTags: [
        {type: "Property", id: "LIST"},
        "Property",
        "PropertyPhoto",
      ],
    }),

    // Get Property Stats
    getPropertyStats: builder.query<PropertyStatsDto, void>({
      query: () => "/properties/stats",
      transformResponse: (response: any) => {
        if (response.success && response.data) {
          return response.data;
        }
        return response.data;
      },
      providesTags: ["Property"],
    }),

    // Get Property by ID
    getPropertyById: builder.query<PropertyResponseDto, number>({
      query: (id) => `/properties/${id}`,
      transformResponse: (response: any) => {
        if (response.success && response.data) {
          // Transform price from string to number
          const property = response.data;
          return {
            ...property,
            price: parseFloat(property.price) || 0,
            bedrooms: parseInt(property.bedrooms) || 0,
            bathrooms: parseInt(property.bathrooms) || 0,
            reads: parseInt(property.reads) || 0,
            latitude: property.latitude ? parseFloat(property.latitude) : null,
            longitude: property.longitude ? parseFloat(property.longitude) : null,
          };
        }
        return response.data;
      },
      providesTags: (result, error, id) => [{type: "Property", id}],
    }),

    // Update Property
    updateProperty: builder.mutation<
      PropertyResponseDto,
      {id: number; data: UpdatePropertyDto}
    >({
      query: ({id, data}) => ({
        url: `/properties/${id}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, {id}) => [
        {type: "Property", id},
        {type: "Property", id: "LIST"},
      ],
    }),

    // Delete Property
    deleteProperty: builder.mutation<
      {success: boolean; message: string},
      number
    >({
      query: (id) => ({
        url: `/properties/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        {type: "Property", id},
        {type: "Property", id: "LIST"},
      ],
    }),

    // Bulk Update Properties
    bulkUpdateProperties: builder.mutation<
      {success: boolean; message: string; updatedCount: number},
      BulkUpdatePropertyDto
    >({
      query: (data) => ({
        url: "/properties/bulk/update",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["Property"],
    }),

    // Bulk Delete Properties
    bulkDeleteProperties: builder.mutation<
      {success: boolean; message: string; deletedCount: number},
      {propertyIds: number[]}
    >({
      query: (data) => ({
        url: "/properties/bulk/delete",
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["Property"],
    }),

    // Activate Property
    activateProperty: builder.mutation<
      {success: boolean; message: string},
      number
    >({
      query: (id) => ({
        url: `/properties/${id}/activate`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        {type: "Property", id},
        {type: "Property", id: "LIST"},
      ],
    }),

    // Deactivate Property
    deactivateProperty: builder.mutation<
      {success: boolean; message: string},
      number
    >({
      query: (id) => ({
        url: `/properties/${id}/deactivate`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        {type: "Property", id},
        {type: "Property", id: "LIST"},
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useCreatePropertyMutation,
  useGetPropertiesQuery,
  useGetPropertyStatsQuery,
  useGetPropertyByIdQuery,
  useUpdatePropertyMutation,
  useDeletePropertyMutation,
  useBulkUpdatePropertiesMutation,
  useBulkDeletePropertiesMutation,
  useActivatePropertyMutation,
  useDeactivatePropertyMutation,
  useCreatePropertyWithPhotosMutation,
} = propertyApi;
